// src/pages/Level-10Layout.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import VirtualPiano from "../components/VirtualPiano";
import RealPianoListener from "../components/RealPianoListener";
import FloatingPianoOverlay from "../components/FloatingPianoOverlay";
import StreamVideo from "../components/StreamVideo";
import Confetti from "react-confetti";

import useBossBattleEngine from "../hooks/useBossBattleEngine";

// ✅ Trophy system
import { TrophyStorage } from "../services/trophyStorage";

// ✅ Reuse helpers from LevelTemplate
import {
  normalizeIncomingNote,
  notesMatch,
  computeProgressFromMatched,
} from "./LevelTemplate";

/* ─────────────────────────────────────────────
   Optional SFX (place files in public/sfx/)
   ───────────────────────────────────────────── */
const SFX = {
  whoosh: "/sfx/whoosh.mp3",
  thud: "/sfx/thud.mp3",
};

function safePlaySfx(src, { volume = 0.8 } = {}) {
  try {
    if (!src) return;
    const a = new Audio(src);
    a.volume = Math.max(0, Math.min(1, volume));
    a.play().catch(() => {});
  } catch {}
}

/* ─────────────────────────────────────────────
   Image helpers (LIVE vs LOCAL safety)
   ───────────────────────────────────────────── */

function buildImageCandidates(src) {
  if (!src || typeof src !== "string") return [];
  const out = [];

  // 1) original
  out.push(src);

  // 2) no querystring variant
  try {
    const [pathOnly] = src.split("?");
    if (pathOnly && pathOnly !== src) out.push(pathOnly);
  } catch {}

  // 3) encode spaces
  if (src.includes(" ")) out.push(src.replace(/ /g, "%20"));

  // 4) lowercase filename variant
  try {
    const [path, query = ""] = src.split("?");
    const parts = path.split("/");
    const file = parts.pop();
    if (file) {
      const lowerFile = file.toLowerCase();
      const lowerPath = [...parts, lowerFile].join("/");
      out.push(query ? `${lowerPath}?${query}` : lowerPath);
      out.push(lowerPath);
    }
  } catch {}

  // 5) fallback guesses if src passed as filename
  if (!src.startsWith("/") && !src.startsWith("http")) {
    out.push(`/images/${src}`);
    out.push(`/images/${encodeURIComponent(src)}`);
  }

  return Array.from(new Set(out));
}

/* ─────────────────────────────────────────────
   Small UI helpers
   ───────────────────────────────────────────── */

function PillButton({ children, className = "", ...rest }) {
  return (
    <button
      type="button"
      className={
        "px-4 py-1.5 rounded-full bg-[#FFC94E] border-[3px] border-[#456EFF] shadow-[0_6px_0_#E0862B] " +
        "text-[10px] sm:text-xs md:text-sm font-extrabold uppercase text-slate-900 active:translate-y-[2px] " +
        "active:shadow-[0_4px_0_#E0862B] transition-transform " +
        className
      }
      {...rest}
    >
      {children}
    </button>
  );
}

function TopNav() {
  return (
    <header className="w-full bg-white/95 shadow-sm">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2 flex items-center">
        <div className="text-base sm:text-lg font-extrabold text-[#1D4ED8]">
          Treble Town
        </div>
        <div className="flex-1" />
        <nav className="flex items-center gap-3 sm:gap-4 text-[10px] sm:text-sm font-semibold text-slate-700">
          <button className="hover:text-[#4F46E5]">PREMIUM</button>
          <button className="hover:text-[#4F46E5] hidden sm:inline">
            PARENTS
          </button>
          <button className="hover:text-[#4F46E5]">STORE</button>
        </nav>
      </div>
    </header>
  );
}

/* ─────────────────────────────────────────────
   Health bars (MOBILE top HUD)
   ───────────────────────────────────────────── */

function DoronHealthBar({ hp, maxHp, className = "" }) {
  const pct = maxHp ? Math.max(0, Math.min(100, (hp / maxHp) * 100)) : 0;
  return (
    <div className={"w-full " + className}>
      <div className="h-3.5 sm:h-4 rounded-full bg-red-600/90 overflow-hidden border border-red-700 shadow-inner">
        <div
          className="h-full bg-emerald-400 transition-[width] duration-200 ease-out"
          style={{ width: `${pct}%` }}
          aria-label="Doron health"
        />
      </div>
    </div>
  );
}

function UserHealthBar({ hp, maxHp, shake, className = "" }) {
  const pct = maxHp ? Math.max(0, Math.min(100, (hp / maxHp) * 100)) : 0;
  return (
    <div
      className={
        "w-full flex items-center gap-2 " +
        className +
        " " +
        (shake ? "tt-hp-shake" : "")
      }
    >
      <div className="text-[14px] sm:text-base">❤️</div>
      <div className="flex-1">
        <div className="h-3 sm:h-3.5 rounded-full bg-slate-200 overflow-hidden border border-slate-300">
          <div
            className="h-full bg-emerald-500 transition-[width] duration-200 ease-out"
            style={{ width: `${pct}%` }}
            aria-label="User health"
          />
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Vertical bars (DESKTOP rails)
   ───────────────────────────────────────────── */

function VerticalBar({ value, max, kind = "doron" }) {
  const pct = max ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;

  const track =
    kind === "doron"
      ? "bg-red-700/90 border-red-800"
      : "bg-slate-200 border-slate-300";

  const fill = kind === "doron" ? "bg-emerald-400" : "bg-emerald-500";

  return (
    <div
      className={`w-3.5 h-32 rounded-full overflow-hidden border shadow-inner flex items-end ${track}`}
    >
      <div
        className={`w-full ${fill} transition-[height] duration-200 ease-out`}
        style={{ height: `${pct}%` }}
      />
    </div>
  );
}

function DesktopLeftUserRail({ userHP, userMaxHP, userShake }) {
  return (
    <aside className="hidden lg:flex flex-col items-center gap-4 w-24 pt-2">
      <div
        className={
          "flex flex-col items-center gap-2 " + (userShake ? "tt-hp-shake" : "")
        }
      >
        <div className="text-xs font-black">❤️</div>
        <VerticalBar value={userHP} max={userMaxHP} kind="user" />
        <div className="text-[10px] font-extrabold text-slate-600 uppercase tracking-wide">
          You
        </div>
      </div>
    </aside>
  );
}

function DesktopRightDoronRail({ doronHP, doronMaxHP }) {
  return (
    <aside className="hidden lg:flex flex-col items-center gap-4 w-24 pt-2">
      <div className="flex flex-col items-center gap-2">
        <div className="text-xs font-black">🐉</div>
        <VerticalBar value={doronHP} max={doronMaxHP} kind="doron" />
        <div className="text-[10px] font-extrabold text-slate-600 uppercase tracking-wide">
          Doron
        </div>
      </div>
    </aside>
  );
}

/* ─────────────────────────────────────────────
   Boss arena
   ───────────────────────────────────────────── */

function BattleField({
  doronSrc,
  doronFilter,
  twinkleSleeping,
  projectiles,
  fireballs,
  localFireballs,
  doronHit,
  finalBoom,
  onDoronImageError,
}) {
  const ARENA_TOP = "#FBF7E4";
  const ARENA_MID = "#F4EFD6";
  const ARENA_BOT = "#E7DCB6";

  return (
    <div className="w-full rounded-[22px] overflow-hidden shadow border border-slate-900/10">
      <div
        className="relative w-full h-[175px] sm:h-[205px] lg:h-[270px]"
        style={{
          background: `linear-gradient(to bottom, ${ARENA_TOP}, ${ARENA_MID}, ${ARENA_BOT})`,
        }}
      >
        <div className="pointer-events-none absolute inset-0 z-[1]">
          <div className="absolute inset-[10px] rounded-[20px] border border-slate-900/14 shadow-[inset_0_0_0_2px_rgba(255,255,255,0.18)]" />
          <div className="absolute inset-[16px] rounded-[18px] border border-slate-900/10 opacity-80" />
          <div className="absolute left-[14px] top-[14px] w-6 h-6 rounded-[10px] border border-slate-900/10 bg-white/10" />
          <div className="absolute right-[14px] top-[14px] w-6 h-6 rounded-[10px] border border-slate-900/10 bg-white/10" />
          <div className="absolute left-[14px] bottom-[14px] w-6 h-6 rounded-[10px] border border-slate-900/10 bg-white/10" />
          <div className="absolute right-[14px] bottom-[14px] w-6 h-6 rounded-[10px] border border-slate-900/10 bg-white/10" />
        </div>

        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at center, rgba(255,255,255,0.34), rgba(0,0,0,0.08))",
          }}
        />
        <div
          className="absolute inset-0 mix-blend-multiply"
          style={{
            opacity: 0.13,
            background:
              "radial-gradient(circle at 20% 30%, rgba(0,0,0,0.20), transparent 55%), radial-gradient(circle at 80% 70%, rgba(0,0,0,0.14), transparent 55%)",
          }}
        />

        {/* Effects layer */}
        <div className="pointer-events-none absolute inset-0 z-[6]">
          {projectiles?.map((p) => (
            <div
              key={p.id}
              className="absolute left-1/2 top-[65%] -translate-x-1/2 text-xl animate-[ttNoteUp_800ms_ease-out_forwards]"
            >
              🎵
            </div>
          ))}

          {/* Engine fireballs (timeouts etc) */}
          {fireballs?.map((f) => (
            <div
              key={f.id}
              className="absolute left-1/2 top-[20%] -translate-x-1/2 text-2xl animate-[ttFireToCamera_1300ms_ease-out_forwards]"
            >
              🔥
            </div>
          ))}

          {/* Local fireballs (Play Now harmless + wrong-note) */}
          {localFireballs?.map((f) => (
            <div
              key={f.id}
              className={
                "absolute left-1/2 top-[20%] -translate-x-1/2 text-2xl " +
                (f.harmless
                  ? "animate-[ttFireToCameraSoft_1300ms_ease-out_forwards]"
                  : "animate-[ttFireToCamera_1300ms_ease-out_forwards]")
              }
            >
              🔥
            </div>
          ))}
        </div>

        <div className="relative z-[7] w-full h-full flex items-center justify-center">
          <img
            src={doronSrc}
            alt=""
            onError={onDoronImageError}
            className={
              "h-28 sm:h-36 lg:h-52 object-contain drop-shadow-[0_18px_24px_rgba(0,0,0,0.30)] " +
              (doronHit ? "tt-doron-hit" : "") +
              (finalBoom ? " tt-final-boom" : "")
            }
            style={{
              filter: twinkleSleeping ? "grayscale(100%)" : doronFilter,
              transition: "filter 220ms ease",
            }}
          />
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Melody board
   ───────────────────────────────────────────── */

function BossMelodyBoard({
  melodyLines,
  progress,
  matchedCount,
  totalNotes,
  isComplete,
  hasStarted,
  isListening,
  detectorDisplay,
  micLocked,
  micLockedTooltip,
  onMicToggle,
  onStartPlay,
  onStopListening,
  showKeyGlow,
  onToggleKeyGlow,
  pianoMode,
  onDocked,
  onFloating,
}) {
  const lineIndex = progress?.line ?? 0;
  const noteIndex = progress?.note ?? 0;
  const line = melodyLines?.[lineIndex];

  if (!line) {
    return (
      <div className="mt-2 w-full bg-white rounded-3xl shadow-md px-3 py-3 border border-slate-100">
        <div className="text-center text-xs text-slate-600">
          Melody data missing.
        </div>
      </div>
    );
  }

  const done = Math.min(matchedCount, totalNotes);
  const pct = totalNotes
    ? Math.max(0, Math.min(100, (done / totalNotes) * 100))
    : 0;

  return (
    <div className="w-full bg-white rounded-3xl shadow-md px-3 sm:px-4 py-2.5 border border-slate-100">
      <div className="flex flex-col items-center">
        {!hasStarted && (
          <div className="mb-2 flex flex-col items-center gap-1">
            <button
              onClick={onStartPlay}
              className="px-8 sm:px-10 py-2.5 sm:py-3 rounded-full bg-[#22C55E] text-white shadow-[0_6px_0_#16A34A] text-xs sm:text-lg font-black tracking-wide animate-pulse"
            >
              🎤 Play Now
            </button>
            <div className="text-[9px] sm:text-[10px] text-slate-600">
              Turn on your microphone or use the virtual piano.
            </div>
          </div>
        )}

        <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2">
          {line.notes.map((n, i) => {
            const status =
              isComplete || i < noteIndex
                ? "done"
                : i === noteIndex
                ? "current"
                : "pending";

            const base =
              "w-9 h-9 sm:w-11 sm:h-11 rounded-lg flex items-center justify-center font-extrabold " +
              "text-[10px] sm:text-sm transition-all select-none";

            let cls = "bg-slate-100 text-slate-700";
            if (status === "done") cls = "bg-[#6EE7B7] text-slate-900";
            if (status === "current")
              cls =
                "bg-white border-2 border-[#4F46E5] text-[#4F46E5] shadow-sm animate-pulse";

            return (
              <div key={`${n}-${i}`} className={`${base} ${cls}`}>
                {String(n).replace("4", "").replace("5", "")}
              </div>
            );
          })}
        </div>

        <div className="mt-2 w-full flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex flex-col">
              <span className="uppercase tracking-[0.18em] text-[8px] text-slate-500">
                Line {lineIndex + 1} / {melodyLines.length}
              </span>
              <span className="text-[9px] text-slate-400">Detector</span>
            </div>

            <div className="inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#EEF2FF] border-2 border-[#4F46E5] text-[#4F46E5] font-extrabold text-sm shadow-sm">
              {detectorDisplay}
            </div>

            {hasStarted && (
              <button
                type="button"
                onClick={micLocked ? undefined : onMicToggle}
                disabled={micLocked}
                className={
                  "ml-1 inline-flex items-center gap-1 rounded-full px-2.5 sm:px-3 py-1 border text-[9px] sm:text-[10px] font-extrabold uppercase shadow-sm transition " +
                  (micLocked
                    ? "bg-slate-100 text-slate-400 border-slate-200 opacity-80 cursor-not-allowed"
                    : isListening
                    ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                    : "bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200")
                }
                title={micLocked ? micLockedTooltip : "Toggle microphone"}
              >
                {micLocked
                  ? "🎤 MIC: LOCKED"
                  : isListening
                  ? "🎤 MIC: ON"
                  : "🎤 MIC: OFF"}
              </button>
            )}
          </div>

          {hasStarted && isListening && (
            <button
              onClick={onStopListening}
              className="px-3 sm:px-4 py-1 rounded-full bg-slate-900 text-white text-[10px] font-semibold shadow"
            >
              Stop
            </button>
          )}
        </div>

        <div className="mt-1 w-full">
          <div className="h-2.5 sm:h-3 rounded-full bg-slate-200 overflow-hidden border border-slate-100">
            <div
              className={
                "h-full rounded-full transition-[width] duration-200 ease-out " +
                (isComplete ? "bg-emerald-400" : "bg-[#4F46E5]")
              }
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        <div className="mt-2 w-full flex flex-wrap items-center justify-center gap-2">
          <label className="inline-flex items-center gap-1 cursor-pointer text-[10px] sm:text-xs text-slate-600">
            <input
              type="checkbox"
              className="h-3 w-3 sm:h-4 sm:w-4 accent-[#3B82F6]"
              checked={showKeyGlow}
              onChange={(e) => onToggleKeyGlow(e.target.checked)}
            />
            <span>Show key glow (hint)</span>
          </label>

          <span className="mx-1 text-slate-300">|</span>

          <PillButton
            className={
              "px-3 py-1 text-[9px] sm:text-[10px] " +
              (pianoMode === "docked" ? "opacity-100" : "opacity-70")
            }
            onClick={onDocked}
          >
            Docked
          </PillButton>

          <PillButton
            className={
              "px-3 py-1 text-[9px] sm:text-[10px] " +
              (pianoMode === "floating" ? "opacity-100" : "opacity-70")
            }
            onClick={onFloating}
          >
            Floating
          </PillButton>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Scene page
   ───────────────────────────────────────────── */

function LevelScene({
  videoSrc,
  onStart,
  startLabel = "Start Game",
  doronHP,
  doronMaxHP,
  userHP,
  userMaxHP,
}) {
  const [videoError, setVideoError] = useState(false);

  const isStream =
    typeof videoSrc === "string" && videoSrc.startsWith("stream:");
  const streamId = isStream ? videoSrc.replace(/^stream:/, "").trim() : null;
  const hasVideo =
    Boolean(videoSrc) && !videoError && (!isStream || Boolean(streamId));

  return (
    <div className="flex-1 px-2 sm:px-3 lg:px-4 pb-6 pt-3">
      <div className="flex flex-col lg:flex-row gap-3 lg:gap-4">
        <DesktopLeftUserRail
          userHP={userHP}
          userMaxHP={userMaxHP}
          userShake={false}
        />

        <div className="flex-1 flex flex-col items-center gap-4 sm:gap-5">
          <div className="lg:hidden w-full">
            <DoronHealthBar
              hp={doronHP}
              maxHp={doronMaxHP}
              className="max-w-3xl mx-auto"
            />
            <div className="mt-2 max-w-3xl mx-auto">
              <UserHealthBar hp={userHP} maxHp={userMaxHP} shake={false} />
            </div>
          </div>

          <div className="relative w-full max-w-4xl mx-auto">
            <div className="absolute inset-0 blur-xl bg-gradient-to-tr from-[#7BD5FF] via-[#A78BFA] to-[#FFCF71] opacity-70 pointer-events-none" />
            <div className="relative z-[1] aspect-video rounded-[24px] sm:rounded-[32px] shadow-[0_20px_40px_rgba(15,23,42,0.4)] overflow-hidden bg-slate-900">
              {hasVideo ? (
                isStream ? (
                  <StreamVideo
                    videoId={streamId}
                    title="Final Battle Intro"
                    className="w-full h-full"
                  />
                ) : (
                  <video
                    className="w-full h-full object-cover"
                    src={videoSrc}
                    controls
                    playsInline
                    controlsList="nodownload"
                    onError={() => setVideoError(true)}
                  />
                )
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-slate-900/80">
                  <div className="text-center px-4">
                    <div className="text-white font-extrabold text-sm sm:text-base">
                      Level 10 Intro Video (coming soon)
                    </div>
                    <div className="text-white/70 text-[10px] sm:text-xs mt-1">
                      Add your Cloudflare Stream ID later — this page still comes
                      first.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <PillButton
            className="px-10 sm:px-16 py-3 text-xs sm:text-sm"
            onClick={onStart}
          >
            {startLabel}
          </PillButton>
        </div>

        <DesktopRightDoronRail doronHP={doronHP} doronMaxHP={doronMaxHP} />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Victory Modal
   ───────────────────────────────────────────── */

function VictoryModal({
  open,
  eligibility,
  onCollect,
  onClose,
  onViewCabinet,
  onHome,
  busy,
  collected,
  errorText,
}) {
  if (!open) return null;

  const ok = !!eligibility?.ok;
  const starsDone = eligibility?.starsDone ?? 0;
  const starsNeeded = eligibility?.starsNeeded ?? 8;

  const levelsDone = eligibility?.levelsDone ?? 0;
  const levelsNeeded = eligibility?.levelsNeeded ?? 10;

  const missingLevels = Array.isArray(eligibility?.missingLevels)
    ? eligibility.missingLevels
    : [];

  return (
    <div className="absolute inset-0 z-[80] flex items-center justify-center bg-slate-900/55 px-3">
      <div className="w-full max-w-md rounded-[26px] bg-white shadow-2xl border border-white/60 overflow-hidden">
        <div className="p-5 sm:p-6 text-center">
          <div className="text-4xl">🏆</div>
          <div className="mt-2 text-xl sm:text-2xl font-black text-slate-900">
            You defeated Doron!
          </div>
          <div className="mt-1 text-xs sm:text-sm text-slate-600 font-semibold">
            Doron has turned into a stone statue. Collect your trophy to finish
            the full journey.
          </div>

          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3 text-left">
            <div className="text-[11px] font-extrabold text-slate-700 uppercase tracking-wide">
              Completion checklist
            </div>

            <div className="mt-2 flex items-center justify-between text-sm">
              <span className="font-bold text-slate-700">Levels</span>
              <span className="font-extrabold text-slate-900">
                {levelsDone}/{levelsNeeded}{" "}
                {levelsDone >= levelsNeeded ? "✅" : "⏳"}
              </span>
            </div>

            <div className="mt-1 flex items-center justify-between text-sm">
              <span className="font-bold text-slate-700">Stars</span>
              <span className="font-extrabold text-slate-900">
                {starsDone}/{starsNeeded}{" "}
                {starsDone >= starsNeeded ? "✅" : "⏳"}
              </span>
            </div>

            {!ok && missingLevels.length > 0 && (
              <div className="mt-2 text-[11px] text-slate-600">
                Missing levels:{" "}
                <span className="font-bold text-slate-800">
                  {missingLevels.join(", ")}
                </span>
              </div>
            )}
          </div>

          {errorText && (
            <div className="mt-3 text-xs font-bold text-red-600">
              {errorText}
            </div>
          )}

          {!collected ? (
            <div className="mt-5 flex flex-col gap-2">
              <button
                type="button"
                onClick={ok && !busy ? onCollect : undefined}
                disabled={!ok || busy}
                className={
                  "w-full px-5 py-3 rounded-2xl font-black text-sm shadow " +
                  (ok && !busy
                    ? "bg-emerald-500 text-white shadow-[0_6px_0_#0F9D58] active:translate-y-[2px] active:shadow-[0_4px_0_#0F9D58]"
                    : "bg-slate-200 text-slate-500 cursor-not-allowed")
                }
              >
                {busy ? "Collecting..." : "🗿 Collect Statue"}
              </button>

              <button
                type="button"
                onClick={onClose}
                className="w-full px-5 py-2.5 rounded-2xl font-extrabold text-xs bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
              >
                Not now
              </button>
            </div>
          ) : (
            <div className="mt-5 flex flex-col gap-2">
              <button
                type="button"
                onClick={onViewCabinet}
                className="w-full px-5 py-3 rounded-2xl font-black text-sm bg-[#4F46E5] text-white shadow-[0_6px_0_#3730A3] active:translate-y-[2px] active:shadow-[0_4px_0_#3730A3]"
              >
                🏆 View Trophy Cabinet
              </button>
              <button
                type="button"
                onClick={onHome}
                className="w-full px-5 py-2.5 rounded-2xl font-extrabold text-xs bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
              >
                🏠 Back Home
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   HP=0 Modal (hard stop)
   ───────────────────────────────────────────── */

function KnockoutModal({ open, onTwinkle, onRestart }) {
  if (!open) return null;
  return (
    <div className="absolute inset-0 z-[85] flex items-center justify-center bg-slate-900/55 px-3">
      <div className="w-full max-w-sm rounded-[26px] bg-white shadow-2xl border border-white/60 overflow-hidden">
        <div className="p-5 sm:p-6 text-center">
          <div className="text-4xl">🔥</div>
          <div className="mt-2 text-xl sm:text-2xl font-black text-slate-900">
            Oh no… you’re out of hearts!
          </div>
          <div className="mt-1 text-xs sm:text-sm text-slate-600 font-semibold">
            Use the <span className="font-black">Twinkle Spell</span> to make
            Doron sleep and restart the battle.
          </div>

          <div className="mt-5 flex flex-col gap-2">
            <button
              type="button"
              onClick={onTwinkle}
              className="w-full px-5 py-3 rounded-2xl font-black text-sm bg-[#FFC94E] border-[3px] border-[#456EFF] text-slate-900 shadow-[0_6px_0_#E0862B] active:translate-y-[2px] active:shadow-[0_4px_0_#E0862B]"
            >
              ✨ Use Twinkle Spell
            </button>

            <button
              type="button"
              onClick={onRestart}
              className="w-full px-5 py-2.5 rounded-2xl font-extrabold text-xs bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
            >
              🔁 Restart Battle
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Main Layout
   ───────────────────────────────────────────── */

export default function Level10Layout({
  levelId = 10,
  videoSrc = null,

  melodyLines = [],
  doronImageSrc,
  doronSleepImageSrc,

  firstKeyOverride = "C4",
  lastKeyOverride = "E5",
}) {
  const navigate = useNavigate();

  const totalNotes = useMemo(
    () => (melodyLines || []).reduce((s, l) => s + (l?.notes?.length ?? 0), 0),
    [melodyLines]
  );

  const flatMelody = useMemo(
    () => (melodyLines || []).flatMap((l) => l?.notes ?? []),
    [melodyLines]
  );

  const [view, setView] = useState("scene");

  const [matchedCount, setMatchedCount] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  const [isListening, setIsListening] = useState(false);
  const [lastHeardNote, setLastHeardNote] = useState(null);

  const [showKeyGlow, setShowKeyGlow] = useState(true);
  const [pianoMode, setPianoMode] = useState("docked");
  const [floatingPrefs, setFloatingPrefs] = useState({
    size: "l",
    x: null,
    y: null,
  });

  const [showConfetti, setShowConfetti] = useState(false);

  // ✅ Victory modal state
  const [showVictory, setShowVictory] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [collectBusy, setCollectBusy] = useState(false);
  const [collectDone, setCollectDone] = useState(false);
  const [collectError, setCollectError] = useState("");

  // ✅ local fireballs
  const [localFireballs, setLocalFireballs] = useState([]);
  const fireballIdRef = useRef(1);

  const spawnLocalFireball = useCallback(({ harmless = false } = {}) => {
    const id = fireballIdRef.current++;
    const item = { id, harmless };
    setLocalFireballs((prev) => [...prev, item]);
    window.setTimeout(() => {
      setLocalFireballs((prev) => prev.filter((x) => x.id !== id));
    }, 1400);
  }, []);

  // ✅ Doron hit shake on correct notes
  const [doronHit, setDoronHit] = useState(false);
  const doronHitTimeoutRef = useRef(null);
  const triggerDoronHit = useCallback(() => {
    setDoronHit(true);
    safePlaySfx(SFX.thud, { volume: 0.85 });
    if (doronHitTimeoutRef.current) clearTimeout(doronHitTimeoutRef.current);
    doronHitTimeoutRef.current = setTimeout(() => {
      setDoronHit(false);
      doronHitTimeoutRef.current = null;
    }, 180);
  }, []);
  useEffect(() => {
    return () => {
      if (doronHitTimeoutRef.current) clearTimeout(doronHitTimeoutRef.current);
    };
  }, []);

  // ✅ Final hit slow-mo boom
  const [finalBoom, setFinalBoom] = useState(false);
  const finalBoomTimeoutRef = useRef(null);
  const triggerFinalBoom = useCallback(() => {
    setFinalBoom(true);
    if (finalBoomTimeoutRef.current) clearTimeout(finalBoomTimeoutRef.current);
    finalBoomTimeoutRef.current = setTimeout(() => {
      setFinalBoom(false);
      finalBoomTimeoutRef.current = null;
    }, 350);
  }, []);
  useEffect(() => {
    return () => {
      if (finalBoomTimeoutRef.current) clearTimeout(finalBoomTimeoutRef.current);
    };
  }, []);

  // ✅ Boss tuning
  const boss = useBossBattleEngine({
    totalNotes,
    timeLimitMs: 3000,
    micTimeLimitMs: 4000,
    userMaxHP: 10,
    doronMaxHP: 100,
    enabled: view === "melody" && hasStarted && !isComplete && !showVictory,
    sleepMs: 1200,
  });

  const isDead =
    boss.userHP <= 0 && !boss.twinkleSleeping && !isComplete && !showVictory;

  const micLockedTooltip =
    "Mic disabled — restart level to play on real piano.";
  const [micLocked, setMicLocked] = useState(false);

  const [userHpShake, setUserHpShake] = useState(false);
  const prevUserHpRef = useRef(boss.userHP);
  const hpShakeTimeoutRef = useRef(null);

  // detect user hp drops -> shake + whoosh when engine spawns fireballs
  const prevFireballCountRef = useRef(0);
  useEffect(() => {
    const prev = prevUserHpRef.current;
    const current = boss.userHP;

    if (
      typeof prev === "number" &&
      typeof current === "number" &&
      current < prev
    ) {
      setUserHpShake(true);
      if (hpShakeTimeoutRef.current) clearTimeout(hpShakeTimeoutRef.current);
      hpShakeTimeoutRef.current = setTimeout(() => {
        setUserHpShake(false);
        hpShakeTimeoutRef.current = null;
      }, 220);
    }

    prevUserHpRef.current = current;

    const fbCount = Array.isArray(boss.fireballs) ? boss.fireballs.length : 0;
    if (fbCount > (prevFireballCountRef.current || 0)) {
      safePlaySfx(SFX.whoosh, { volume: 0.7 });
    }
    prevFireballCountRef.current = fbCount;
  }, [boss.userHP, boss.fireballs]);

  useEffect(() => {
    return () => {
      if (hpShakeTimeoutRef.current) clearTimeout(hpShakeTimeoutRef.current);
    };
  }, []);

  const progress = useMemo(
    () => computeProgressFromMatched(melodyLines, matchedCount, totalNotes),
    [melodyLines, matchedCount, totalNotes]
  );

  const expectedRaw = useMemo(() => {
    const li = progress?.line ?? 0;
    const ni = progress?.note ?? 0;
    return melodyLines?.[li]?.notes?.[ni] ?? null;
  }, [melodyLines, progress]);

  const expectedPc = useMemo(
    () => (expectedRaw ? normalizeIncomingNote(expectedRaw) : ""),
    [expectedRaw]
  );

  const statueProgress = useMemo(() => {
    if (!totalNotes) return 0;
    return Math.max(0, Math.min(1, matchedCount / totalNotes));
  }, [matchedCount, totalNotes]);

  const [windowSize, setWindowSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 1024,
    height: typeof window !== "undefined" ? window.innerHeight : 768,
  });

  useEffect(() => {
    const fn = () =>
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  const pianoConfig = useMemo(() => {
    const w = windowSize.width;
    if (w < 350)
      return {
        keyWidth: 22,
        whiteKeyHeight: 96,
        blackKeyWidth: 14,
        blackKeyHeight: 58,
      };
    if (w < 390)
      return {
        keyWidth: 24,
        whiteKeyHeight: 104,
        blackKeyWidth: 16,
        blackKeyHeight: 64,
      };
    if (w < 480)
      return {
        keyWidth: 28,
        whiteKeyHeight: 118,
        blackKeyWidth: 19,
        blackKeyHeight: 76,
      };
    if (w < 768)
      return {
        keyWidth: 38,
        whiteKeyHeight: 150,
        blackKeyWidth: 24,
        blackKeyHeight: 96,
      };
    if (w < 1024)
      return {
        keyWidth: 50,
        whiteKeyHeight: 170,
        blackKeyWidth: 30,
        blackKeyHeight: 110,
      };
    return {
      keyWidth: 62,
      whiteKeyHeight: 200,
      blackKeyWidth: 36,
      blackKeyHeight: 128,
    };
  }, [windowSize.width]);

  const virtualPianoScale = useMemo(() => {
    const w = windowSize.width;
    if (w < 350) return 0.92;
    if (w < 390) return 0.95;
    if (w < 520) return 1.05;
    if (w < 768) return 1.08;
    return 1.15;
  }, [windowSize.width]);

  const pianoContainerMinHeight = useMemo(() => {
    const base = pianoConfig.whiteKeyHeight || 160;
    return Math.ceil(base * virtualPianoScale + 56);
  }, [pianoConfig.whiteKeyHeight, virtualPianoScale]);

  const detectorLastUiAtRef = useRef(0);
  const detectorLastPcRef = useRef(null);

  const setDetectorNoteThrottled = useCallback((pc) => {
    if (!pc) return;
    const now =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    if (
      detectorLastPcRef.current === pc &&
      now - detectorLastUiAtRef.current < 90
    )
      return;
    detectorLastPcRef.current = pc;
    detectorLastUiAtRef.current = now;
    setLastHeardNote(pc);
  }, []);

  const toggleMic = useCallback(() => {
    const btn = document.querySelector("#level10-mic-control button");
    if (btn) btn.click();
    setIsListening((p) => !p);
  }, []);

  // ✅ Doron image fallback
  const doronCandidates = useMemo(
    () => buildImageCandidates(doronImageSrc || "/images/DORON.png"),
    [doronImageSrc]
  );
  const sleepCandidates = useMemo(
    () =>
      buildImageCandidates(
        doronSleepImageSrc || "/images/doron-sleeping.png"
      ),
    [doronSleepImageSrc]
  );

  const [doronIdx, setDoronIdx] = useState(0);
  const [sleepIdx, setSleepIdx] = useState(0);

  useEffect(() => setDoronIdx(0), [doronImageSrc]);
  useEffect(() => setSleepIdx(0), [doronSleepImageSrc]);

  const handleDoronImageError = useCallback(() => {
    if (boss.twinkleSleeping) {
      setSleepIdx((i) => {
        const next = i + 1;
        return next < sleepCandidates.length ? next : i;
      });
    } else {
      setDoronIdx((i) => {
        const next = i + 1;
        return next < doronCandidates.length ? next : i;
      });
    }
  }, [boss.twinkleSleeping, doronCandidates.length, sleepCandidates.length]);

  const handleStartPlay = useCallback(() => {
    if (!hasStarted) setHasStarted(true);
    if (micLocked) return;
    if (isDead) return;

    spawnLocalFireball({ harmless: true });
    safePlaySfx(SFX.whoosh, { volume: 0.55 });

    boss.setInputMode("mic");
    if (!isListening) toggleMic();
  }, [
    hasStarted,
    micLocked,
    isListening,
    toggleMic,
    boss,
    spawnLocalFireball,
    isDead,
  ]);

  const handleStopListening = useCallback(() => {
    if (isListening) toggleMic();
  }, [isListening, toggleMic]);

  const handleMicToggle = useCallback(() => {
    if (!hasStarted) setHasStarted(true);
    if (micLocked) return;
    if (isDead) return;

    boss.setInputMode("mic");
    toggleMic();
  }, [hasStarted, micLocked, toggleMic, boss, isDead]);

  // ✅ KEY GLOW: force SINGLE best key
  useEffect(() => {
    if (typeof document === "undefined") return;

    document.querySelectorAll(".tt-current-key-glow").forEach((el) => {
      el.classList.remove("tt-current-key-glow");
    });

    if (
      !showKeyGlow ||
      view !== "melody" ||
      isComplete ||
      !expectedRaw ||
      showVictory ||
      isDead
    )
      return;

    const raw = String(expectedRaw).trim();
    const pc = expectedPc || normalizeIncomingNote(raw);

    const pickBest = (cands) => {
      if (!cands || cands.length === 0) return null;
      if (cands.length === 1) return cands[0];

      const m = raw.match(/(\d+)/);
      const targetOct = m ? String(m[1]) : null;

      if (targetOct) {
        const withOct = cands.find((el) =>
          String(el.getAttribute("data-note") || "").includes(targetOct)
        );
        if (withOct) return withOct;

        const octNum = parseInt(targetOct, 10);
        if (Number.isFinite(octNum) && octNum >= 5)
          return cands[cands.length - 1];
      }

      return cands[0];
    };

    let chosen = null;

    let candidates = Array.from(
      document.querySelectorAll(`[data-note="${raw}"]`)
    );
    chosen = pickBest(candidates);

    if (!chosen && pc) {
      candidates = Array.from(document.querySelectorAll(`[data-note="${pc}"]`));
      chosen = pickBest(candidates);
    }

    if (!chosen && pc) {
      candidates = Array.from(
        document.querySelectorAll(`[data-note^="${pc}"]`)
      );
      chosen = pickBest(candidates);
    }

    if (chosen) chosen.classList.add("tt-current-key-glow");
  }, [
    showKeyGlow,
    view,
    isComplete,
    expectedRaw,
    expectedPc,
    showVictory,
    isDead,
  ]);

  const restartLevel = useCallback(() => {
    setMatchedCount(0);
    setIsComplete(false);
    setHasStarted(false);
    setLastHeardNote(null);

    setMicLocked(false);

    boss.setInputMode("virtual");
    boss.resetBattle();

    try {
      const btn = document.querySelector("#level10-mic-control button");
      if (btn && isListening) btn.click();
    } catch {}
    setIsListening(false);

    setShowConfetti(false);

    setShowVictory(false);
    setCollectDone(false);
    setCollectBusy(false);
    setCollectError("");
    setEligibility(null);

    setLocalFireballs([]);
    setDoronHit(false);
    setFinalBoom(false);

    setDoronIdx(0);
    setSleepIdx(0);
  }, [boss, isListening]);

  // ✅ Victory trigger + final boom
  useEffect(() => {
    if (!isComplete && totalNotes > 0 && matchedCount >= totalNotes) {
      triggerFinalBoom();

      setIsComplete(true);

      if (levelId === 10) boss.onFullMelodyComplete();

      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 1400);

      if (levelId === 10) {
        TrophyStorage.markLevelComplete(10);

        const e = TrophyStorage.canCollectStatue();
        setEligibility(e);
        setCollectError("");
        setCollectDone(false);

        setShowVictory(true);
      }
    }
  }, [matchedCount, totalNotes, isComplete, boss, levelId, triggerFinalBoom]);

  // when modal opens, stop mic
  useEffect(() => {
    if (!showVictory) return;
    try {
      const btn = document.querySelector("#level10-mic-control button");
      if (btn && isListening) btn.click();
    } catch {}
    setIsListening(false);
  }, [showVictory, isListening]);

  // stop mic if player dies
  useEffect(() => {
    if (!isDead) return;
    try {
      const btn = document.querySelector("#level10-mic-control button");
      if (btn && isListening) btn.click();
    } catch {}
    setIsListening(false);
  }, [isDead, isListening]);

  const handleVirtualKey = useCallback(
    (noteName) => {
      if (showVictory) return;
      if (isDead) return;
      if (!hasStarted) setHasStarted(true);
      if (isComplete) return;

      boss.setInputMode("virtual");

      if (isListening) {
        toggleMic();
        setMicLocked(true);
      }

      const pc = normalizeIncomingNote(noteName);
      setDetectorNoteThrottled(pc);

      if (!expectedRaw) return;

      if (!notesMatch(pc, expectedRaw)) {
        spawnLocalFireball({ harmless: false });
        safePlaySfx(SFX.whoosh, { volume: 0.75 });
        boss.onWrongNote();
        return;
      }

      const nextCount = Math.min(totalNotes, matchedCount + 1);
      if (totalNotes > 0 && nextCount >= totalNotes) {
        triggerFinalBoom();
      } else {
        triggerDoronHit();
      }

      boss.onCorrectNote();
      setMatchedCount((c) => Math.min(totalNotes, c + 1));
    },
    [
      showVictory,
      isDead,
      hasStarted,
      isComplete,
      isListening,
      toggleMic,
      expectedRaw,
      boss,
      totalNotes,
      setDetectorNoteThrottled,
      spawnLocalFireball,
      triggerDoronHit,
      triggerFinalBoom,
      matchedCount,
    ]
  );

  const handleMicNoteDetected = useCallback(
    (note) => {
      if (showVictory) return;
      if (isDead) return;
      const pc = normalizeIncomingNote(note);
      if (!pc) return;
      setDetectorNoteThrottled(pc);
    },
    [setDetectorNoteThrottled, showVictory, isDead]
  );

  const handleMicWrongConfirmed = useCallback(() => {
    return;
  }, []);

  const handleMicNoteMatched = useCallback(
    (arr) => {
      if (showVictory) return;
      if (isDead) return;
      if (!Array.isArray(arr)) return;

      boss.setInputMode("mic");
      const len = Math.min(arr.length, totalNotes);

      setMatchedCount((prev) => {
        if (len <= prev) return prev;
        const delta = len - prev;

        for (let i = 0; i < delta; i++) {
          const upcoming = prev + i + 1;
          if (totalNotes > 0 && upcoming >= totalNotes) {
            triggerFinalBoom();
          } else {
            triggerDoronHit();
          }
          boss.onCorrectNote();
        }

        return len;
      });
    },
    [showVictory, boss, totalNotes, triggerDoronHit, triggerFinalBoom, isDead]
  );

  const handleTwinkleSpell = useCallback(async () => {
    const res = await boss.twinkleSpell();
    if (res?.resetRequested) {
      restartLevel();
      setView("melody");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [boss, restartLevel]);

  const doronSrc = useMemo(() => {
    const list = boss.twinkleSleeping ? sleepCandidates : doronCandidates;
    const idx = boss.twinkleSleeping ? sleepIdx : doronIdx;
    return (
      list?.[idx] ||
      list?.[0] ||
      (boss.twinkleSleeping ? doronSleepImageSrc : doronImageSrc) ||
      ""
    );
  }, [
    boss.twinkleSleeping,
    sleepCandidates,
    doronCandidates,
    sleepIdx,
    doronIdx,
    doronSleepImageSrc,
    doronImageSrc,
  ]);

  const doronFilter = useMemo(() => {
    const g = Math.round(statueProgress * 100);
    const sat = Math.max(0.15, 1 - statueProgress * 0.85);
    const con = 1 + statueProgress * 0.35;
    return `grayscale(${g}%) saturate(${sat}) contrast(${con})`;
  }, [statueProgress]);

  const detectorDisplay = lastHeardNote
    ? normalizeIncomingNote(lastHeardNote) || "—"
    : "—";

  // ✅ KEY CHANGE: after Collect Statue succeeds → go to /trophy-cabinet
  const handleCollectStatue = useCallback(() => {
    setCollectBusy(true);
    setCollectError("");

    try {
      const res = TrophyStorage.completeRound({
        enforceRequirements: true,
        meta: { finalBoss: "Doron", levelId: 10, song: "Fur Elise (Easy)" },
      });

      if (res?.error) {
        const e2 = TrophyStorage.canCollectStatue();
        setEligibility(e2);
        setCollectError(
          "Finish the missing levels + stars to collect the statue."
        );
        setCollectBusy(false);
        return;
      }

      setCollectDone(true);
      setCollectBusy(false);

      // ✅ Your desired flow:
      // Level-10 → Trophy Cabinet → Restart/reset from there
      navigate("/trophy-cabinet", { replace: true });
    } catch (e) {
      setCollectError("Something went wrong. Try again.");
      setCollectBusy(false);
    }
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#EAF6FF] flex flex-col">
      <TopNav />

      {showConfetti && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          numberOfPieces={180}
          recycle={false}
        />
      )}

      <div className="flex-1 flex justify-center">
        <div className="max-w-7xl mx-auto w-full px-2 sm:px-3 py-3 sm:py-4">
          <div className="flex justify-center">
            <div className="flex-1 bg-white/85 rounded-[26px] sm:rounded-[32px] shadow-2xl border border-white/80 flex flex-col overflow-hidden relative">
              <VictoryModal
                open={showVictory}
                eligibility={eligibility}
                busy={collectBusy}
                collected={collectDone}
                errorText={collectError}
                onCollect={handleCollectStatue}
                onClose={() => setShowVictory(false)}
                onViewCabinet={() => navigate("/trophy-cabinet")}
                onHome={() => navigate("/")}
              />

              <KnockoutModal
                open={isDead}
                onTwinkle={handleTwinkleSpell}
                onRestart={() => {
                  restartLevel();
                  setView("melody");
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />

              {view === "scene" ? (
                <LevelScene
                  videoSrc={videoSrc}
                  startLabel="Start Final Battle"
                  doronHP={boss.doronHP}
                  doronMaxHP={boss.doronMaxHP}
                  userHP={boss.userHP}
                  userMaxHP={boss.userMaxHP}
                  onStart={() => {
                    setView("melody");
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                />
              ) : (
                <div className="flex-1 px-2 sm:px-3 lg:px-4 pb-4 pt-3">
                  <div className="flex flex-col lg:flex-row gap-3 lg:gap-4">
                    <DesktopLeftUserRail
                      userHP={boss.userHP}
                      userMaxHP={boss.userMaxHP}
                      userShake={userHpShake}
                    />

                    <div className="flex-1 flex flex-col gap-3">
                      <div className="lg:hidden">
                        <DoronHealthBar
                          hp={boss.doronHP}
                          maxHp={boss.doronMaxHP}
                          className="max-w-3xl mx-auto"
                        />
                        <div className="mt-2 max-w-3xl mx-auto">
                          <UserHealthBar
                            hp={boss.userHP}
                            maxHp={boss.userMaxHP}
                            shake={userHpShake}
                          />
                        </div>
                      </div>

                      <div className="max-w-5xl mx-auto w-full">
                        <BattleField
                          doronSrc={doronSrc}
                          doronFilter={doronFilter}
                          twinkleSleeping={boss.twinkleSleeping}
                          projectiles={boss.projectiles}
                          fireballs={boss.fireballs}
                          localFireballs={localFireballs}
                          doronHit={doronHit}
                          finalBoom={finalBoom}
                          onDoronImageError={handleDoronImageError}
                        />
                      </div>

                      <div className="max-w-5xl mx-auto w-full">
                        <BossMelodyBoard
                          melodyLines={melodyLines}
                          progress={progress}
                          matchedCount={matchedCount}
                          totalNotes={totalNotes}
                          isComplete={isComplete}
                          hasStarted={hasStarted}
                          isListening={isListening}
                          detectorDisplay={detectorDisplay}
                          micLocked={micLocked}
                          micLockedTooltip={micLockedTooltip}
                          onMicToggle={handleMicToggle}
                          onStartPlay={handleStartPlay}
                          onStopListening={() => {
                            if (isListening) handleStopListening();
                          }}
                          showKeyGlow={showKeyGlow}
                          onToggleKeyGlow={setShowKeyGlow}
                          pianoMode={pianoMode}
                          onDocked={() => setPianoMode("docked")}
                          onFloating={() => setPianoMode("floating")}
                        />
                      </div>

                      <div className="w-full flex flex-col items-center justify-center">
                        {pianoMode === "docked" && (
                          <div className="w-full max-w-full sm:max-w-5xl flex justify-center relative">
                            {!hasStarted && (
                              <div className="absolute inset-0 z-10 bg-white/60 rounded-3xl flex items-center justify-center">
                                <span className="text-xs text-slate-600">
                                  Tap <strong>Play Now</strong> to begin.
                                </span>
                              </div>
                            )}

                            <div
                              className={
                                "bg-white rounded-[22px] sm:rounded-[26px] shadow-lg border border-slate-100 " +
                                "px-1.5 sm:px-3 py-4 sm:py-6 lg:py-7 w-full flex justify-center items-center " +
                                (!hasStarted || showVictory || isDead
                                  ? "opacity-80 pointer-events-none"
                                  : "")
                              }
                              style={{ minHeight: pianoContainerMinHeight }}
                            >
                              <div
                                className="w-full flex justify-center"
                                style={{
                                  transform: `scale(${virtualPianoScale})`,
                                  transformOrigin: "top center",
                                }}
                              >
                                <VirtualPiano
                                  onNoteClick={handleVirtualKey}
                                  keyWidth={pianoConfig.keyWidth}
                                  whiteKeyHeight={pianoConfig.whiteKeyHeight}
                                  blackKeyWidth={pianoConfig.blackKeyWidth}
                                  blackKeyHeight={pianoConfig.blackKeyHeight}
                                  firstKey={firstKeyOverride}
                                  lastKey={lastKeyOverride}
                                  showAllBlackLabels={true}
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        <FloatingPianoOverlay
                          open={view === "melody" && pianoMode === "floating"}
                          windowSize={windowSize}
                          hasStarted={hasStarted}
                          levelId={levelId}
                          firstKey={firstKeyOverride}
                          lastKey={lastKeyOverride}
                          pianoConfig={pianoConfig}
                          onNoteClick={handleVirtualKey}
                          showKeyGlow={showKeyGlow}
                          onToggleKeyGlow={(v) => setShowKeyGlow(v)}
                          onDock={() => setPianoMode("docked")}
                          initialPrefs={floatingPrefs}
                          onPrefsChange={setFloatingPrefs}
                          showAllBlackLabels={true}
                          blackKeyLabels={{}}
                        />

                        <div
                          id="level10-mic-control"
                          className="w-full flex justify-center"
                        >
                          <RealPianoListener
                            targetMelody={flatMelody}
                            startLabel=""
                            stopLabel=""
                            showMicHint={false}
                            buttonWrapperClassName="hidden"
                            containerClassName="hidden"
                            onNoteDetected={handleMicNoteDetected}
                            onWrongConfirmed={handleMicWrongConfirmed}
                            onNoteMatched={handleMicNoteMatched}
                            onComplete={() => setMatchedCount(totalNotes)}
                            detectionSensitivity={1.25}
                            toleranceCents={85}
                            toleranceHz={14}
                            wrongStableFramesRequired={1}
                            wrongCooldownMs={180}
                            wrongCentsThreshold={70}
                            wrongHzThreshold={7}
                          />
                        </div>

                        <div className="mt-2 w-full max-w-5xl mx-auto flex flex-col sm:flex-row items-stretch justify-between gap-3">
                          <div className="flex flex-wrap justify-center sm:justify-start gap-2 sm:gap-3">
                            <PillButton
                              className="px-3 py-1 text-[9px] sm:text-[10px] flex items-center gap-1"
                              onClick={() => navigate("/")}
                            >
                              🏠 Home
                            </PillButton>

                            <PillButton
                              className="px-3 py-1 text-[9px] sm:text-[10px] flex items-center gap-1"
                              onClick={restartLevel}
                            >
                              🔁 Restart
                            </PillButton>
                          </div>
                        </div>
                      </div>
                    </div>

                    <DesktopRightDoronRail
                      doronHP={boss.doronHP}
                      doronMaxHP={boss.doronMaxHP}
                    />
                  </div>

                  {boss.twinkleSleeping && (
                    <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/40">
                      <div className="bg-white/95 rounded-[26px] shadow-2xl px-6 py-5 max-w-sm text-center">
                        <div className="text-3xl">✨</div>
                        <div className="mt-1 text-lg font-black text-slate-900">
                          Twinkle Spell!
                        </div>
                        <div className="mt-1 text-xs text-slate-600 font-semibold">
                          Doron fell asleep… you get a break. Restarting the
                          battle…
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .tt-current-key-glow {
          box-shadow: 0 0 0 4px rgba(251, 191, 36, 0.7), 0 0 22px rgba(251, 191, 36, 0.55) !important;
          border-radius: 10px;
          position: relative;
          z-index: 2;
        }

        @keyframes ttNoteUp {
          0%   { transform: translate(-50%, 0) scale(1); opacity: 1; }
          100% { transform: translate(-50%, -140px) scale(0.8); opacity: 0; }
        }

        @keyframes ttFireToCamera {
          0%   { transform: translate(-50%, 0) scale(0.92); opacity: 1; filter: drop-shadow(0 0 0 rgba(0,0,0,0)); }
          55%  { transform: translate(-50%, 130px) scale(1.28); opacity: 1; filter: drop-shadow(0 10px 18px rgba(0,0,0,0.25)); }
          100% { transform: translate(-50%, 210px) scale(1.75); opacity: 0; filter: drop-shadow(0 16px 26px rgba(0,0,0,0.38)); }
        }

        @keyframes ttFireToCameraSoft {
          0%   { transform: translate(-50%, 0) scale(0.9); opacity: 0.95; }
          65%  { transform: translate(-50%, 160px) scale(1.35); opacity: 0.75; }
          100% { transform: translate(-50%, 215px) scale(1.6); opacity: 0; }
        }

        @keyframes ttDoronHit {
          0%   { transform: translateX(0) rotate(0deg) scale(1); }
          20%  { transform: translateX(-6px) rotate(-2deg) scale(1.02); }
          45%  { transform: translateX(6px) rotate(2deg) scale(1.03); }
          70%  { transform: translateX(-4px) rotate(-1deg) scale(1.01); }
          100% { transform: translateX(0) rotate(0deg) scale(1); }
        }
        .tt-doron-hit {
          animation: ttDoronHit 180ms ease-in-out;
          filter: drop-shadow(0 0 14px rgba(255, 255, 255, 0.35));
        }

        @keyframes ttFinalBoom {
          0%   { transform: scale(1); filter: drop-shadow(0 0 0 rgba(0,0,0,0)); }
          45%  { transform: scale(0.92); filter: drop-shadow(0 12px 22px rgba(0,0,0,0.28)) brightness(1.05); }
          100% { transform: scale(0.90); filter: drop-shadow(0 18px 28px rgba(0,0,0,0.35)) brightness(1.08); }
        }
        .tt-final-boom {
          animation: ttFinalBoom 350ms ease-out;
        }

        @keyframes ttHpShake {
          0%   { transform: translateX(0); }
          25%  { transform: translateX(-3px); }
          50%  { transform: translateX(3px); }
          75%  { transform: translateX(-2px); }
          100% { transform: translateX(0); }
        }
        .tt-hp-shake {
          animation: ttHpShake 220ms ease-in-out;
        }
      `}</style>
    </div>
  );
}
